import React, { useState, useCallback, useContext, useMemo } from 'react';
import { isLeft } from "fp-ts/lib/Either";
import ScheduleEntity, { ScheduleDay } from '../../../../../domain/entities/ScheduleEntity';
import ButtonComponent from '../../../../components/button/ButtonComponent';
import LocalizationContext from '../../../../providers/localization/LocalizationContext';
import LocalizationContextType from '../../../../../domain/providers/localization/LocalizationContextType';
import ModalsContext from '../../../../providers/modal/ModalsContext';
import ModalsContextType from '../../../../../domain/providers/modal/ModalsContextType';
import di from '../../../../../DependencyInjection';
import DeleteScheduleUseCase, { DeleteScheduleUseCaseName } from '../../../../../domain/use_cases/stablishment/DeleteScheduleUseCase';
import SortSchedulesUseCase, { SortSchedulesUseCaseName } from '../../../../../domain/use_cases/schedule/SortSchedulesUseCase';
import KeyWordLocalization from '../../../../utils/KeyWordLocalization';

interface ScheduleManagementProps {
    schedules: ScheduleEntity[];
    onSchedulesChange: (schedules: ScheduleEntity[]) => void;
    onScheduleDeleted?: (deletedScheduleId: string) => void;
    loading?: boolean;
}

const ScheduleManagement: React.FC<ScheduleManagementProps> = ({
    schedules,
    onSchedulesChange,
    onScheduleDeleted,
    loading = false
}) => {
    const { i18n } = useContext(LocalizationContext) as LocalizationContextType;
    const { addToast, openModalDelete, closeModalDelete } = useContext(ModalsContext) as ModalsContextType;

    // Get the sort use case from DI container
    const sortSchedulesUseCase = di.get<SortSchedulesUseCase>(SortSchedulesUseCaseName);

    // Sort schedules by day and time using useMemo for performance
    const sortedSchedules = useMemo(() => {
        return sortSchedulesUseCase.execute(schedules);
    }, [schedules, sortSchedulesUseCase]);

    const [newSchedule, setNewSchedule] = useState({
        day: '',
        openTime: '',
        closeTime: '',
        // neededStaff: 1
    });
    const [deletingScheduleId, setDeletingScheduleId] = useState<string | null>(null);

    // Función para validar que no se crucen los horarios
    const validateTimeOverlap = useCallback((day: ScheduleDay, openTime: string, closeTime: string, excludeId?: string): boolean => {
        const daySchedules = schedules.filter(schedule =>
            schedule.day === day && schedule.id !== excludeId
        );

        const newStart = new Date(`2000-01-01T${openTime}:00`);
        const newEnd = new Date(`2000-01-01T${closeTime}:00`);

        // Validar que la hora de apertura sea menor que la de cierre
        if (newStart >= newEnd) {
            return false;
        }

        // Verificar solapamiento con horarios existentes
        for (const existingSchedule of daySchedules) {
            const existingStart = new Date(`2000-01-01T${existingSchedule.openTime}:00`);
            const existingEnd = new Date(`2000-01-01T${existingSchedule.closeTime}:00`);

            // Verificar si hay solapamiento
            if (
                (newStart >= existingStart && newStart < existingEnd) ||
                (newEnd > existingStart && newEnd <= existingEnd) ||
                (newStart <= existingStart && newEnd >= existingEnd)
            ) {
                return false;
            }
        }

        return true;
    }, [schedules]);

    const handleAddSchedule = useCallback(() => {
        const { day, openTime, closeTime } = newSchedule;

        // Validaciones
        if (!day || !openTime || !closeTime) {
            addToast(i18n("Todos los campos son obligatorios"), "error", undefined);
            return;
        }

        // if (neededStaff < 1) {
        //     addToast(i18n("El personal necesario debe ser al menos 1"), "error", undefined);
        //     return;
        // }

        if (!validateTimeOverlap(day as ScheduleDay, openTime, closeTime)) {
            addToast(i18n("El horario se cruza con otro existente o la hora de apertura es posterior a la de cierre"), "error", undefined);
            return;
        }

        // Agregar horario
        const schedule = new ScheduleEntity({
            id: `temp-${Date.now()}`,
            day: day as ScheduleDay,
            openTime,
            closeTime,
            // neededStaff: parseInt(neededStaff.toString())
        });

        const updatedSchedules = [...schedules, schedule];
        onSchedulesChange(updatedSchedules);

        // Limpiar formulario
        setNewSchedule({
            day: '',
            openTime: '',
            closeTime: '',
            // neededStaff: 1
        });

        addToast(i18n("Horario agregado exitosamente"), "success", undefined);
    }, [newSchedule, validateTimeOverlap, schedules, onSchedulesChange, addToast, i18n]);

    const handleRemoveSchedule = useCallback((scheduleId: string) => {
        const schedule = schedules.find(s => s.id === scheduleId);
        if (!schedule) return;

        // Si el schedule tiene un ID numérico válido (viene de la base de datos), 
        // confirmar eliminación y llamar a la API
        const isExistingSchedule = scheduleId && !scheduleId.startsWith('temp_') && !isNaN(Number(scheduleId));
        
        if (isExistingSchedule) {
            const confirmDelete = async () => {
                setDeletingScheduleId(scheduleId);
                
                try {
                    const response = await di.get<DeleteScheduleUseCase>(DeleteScheduleUseCaseName).call(scheduleId);
                    
                    if (isLeft(response)) {
                        addToast(response.left.code ?? KeyWordLocalization.UnknownError, 'error', undefined);
                        return;
                    }
                    
                    // Si la eliminación en la API fue exitosa, actualizar el estado local
                    const updatedSchedules = schedules.filter(schedule => schedule.id !== scheduleId);
                    onSchedulesChange(updatedSchedules);
                    
                    // Notificar al componente padre que se eliminó un schedule de la BD
                    if (onScheduleDeleted) {
                        onScheduleDeleted(scheduleId);
                    }
                    
                    closeModalDelete();
                    addToast(i18n("Horario eliminado exitosamente"), "success", undefined);
                } catch (error) {
                    console.error("Error deleting schedule:", error);
                    addToast(i18n("Error al eliminar horario"), "error", undefined);
                } finally {
                    setDeletingScheduleId(null);
                }
            };

            openModalDelete(
                i18n("Eliminar Horario"),
                i18n("¿Estás seguro de que deseas eliminar este horario? Esta acción no se puede deshacer."),
                confirmDelete
            );
        } else {
            // Si es un schedule recién creado (sin ID de BD), solo eliminar del estado local
            const updatedSchedules = schedules.filter(schedule => schedule.id !== scheduleId);
            onSchedulesChange(updatedSchedules);
            addToast(i18n("Horario eliminado"), "success", undefined);
        }
    }, [schedules, onSchedulesChange, onScheduleDeleted, addToast, i18n, openModalDelete, closeModalDelete]);

    const getDayDisplayName = (day: ScheduleDay): string => {
        const dayNames = {
            [ScheduleDay.lunes]: "Lunes",
            [ScheduleDay.martes]: "Martes",
            [ScheduleDay.miercoles]: "Miércoles",
            [ScheduleDay.jueves]: "Jueves",
            [ScheduleDay.viernes]: "Viernes",
            [ScheduleDay.sabado]: "Sábado",
            [ScheduleDay.domingo]: "Domingo"
        };
        return dayNames[day] || day;
    };

    const formatTime = (time: string): string => {
        return new Date(`2000-01-01T${time}:00`).toLocaleTimeString('es-ES', {
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <div className="schedule-management">
            <div className="row">
                <div className="col-12">
                    <h5 className="mb-3">{i18n("Horarios de Funcionamiento")}</h5>

                    {/* Formulario para agregar nuevo horario */}
                    <div className="card mb-4">
                        <div className="card-header">
                            <h6 className="mb-0">{i18n("Agregar Nuevo Horario")}</h6>
                        </div>
                        <div className="card-body">
                            <div className="row">
                                <div className="col-md-3">
                                    <label className="form-label">{i18n("Día")}</label>
                                    <select
                                        className="form-control"
                                        value={newSchedule.day}
                                        onChange={(e) => setNewSchedule(prev => ({ ...prev, day: e.target.value }))}
                                        disabled={loading}
                                    >
                                        <option value="">{i18n("Seleccionar día")}</option>
                                        <option value={ScheduleDay.lunes}>{i18n("Lunes")}</option>
                                        <option value={ScheduleDay.martes}>{i18n("Martes")}</option>
                                        <option value={ScheduleDay.miercoles}>{i18n("Miércoles")}</option>
                                        <option value={ScheduleDay.jueves}>{i18n("Jueves")}</option>
                                        <option value={ScheduleDay.viernes}>{i18n("Viernes")}</option>
                                        <option value={ScheduleDay.sabado}>{i18n("Sábado")}</option>
                                        <option value={ScheduleDay.domingo}>{i18n("Domingo")}</option>
                                    </select>
                                </div>

                                <div className="col-md-3">
                                    <label className="form-label">{i18n("Hora Apertura")}</label>
                                    <input
                                        type="time"
                                        className="form-control"
                                        value={newSchedule.openTime}
                                        onChange={(e) => setNewSchedule(prev => ({ ...prev, openTime: e.target.value }))}
                                        disabled={loading}
                                    />
                                </div>

                                <div className="col-md-3">
                                    <label className="form-label">{i18n("Hora Cierre")}</label>
                                    <input
                                        type="time"
                                        className="form-control"
                                        value={newSchedule.closeTime}
                                        onChange={(e) => setNewSchedule(prev => ({ ...prev, closeTime: e.target.value }))}
                                        disabled={loading}
                                    />
                                </div>

                                {/* <div className="col-md-2">
                                    <label className="form-label">{i18n("Personal Necesario")}</label>
                                    <input
                                        type="number"
                                        min="1"
                                        className="form-control"
                                        value={newSchedule.neededStaff}
                                        onChange={(e) => setNewSchedule(prev => ({ ...prev, neededStaff: parseInt(e.target.value) || 1 }))}
                                        disabled={loading}
                                    />
                                </div> */}

                                <div className="col-md-3 d-flex align-items-end">
                                    <ButtonComponent
                                        onClick={handleAddSchedule}
                                        design="primaryOutline"
                                        text={i18n("Agregar")}
                                        icon={<span className="material-symbols-outlined">add</span>}
                                        disabled={loading}
                                        className="w-100"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Lista de horarios existentes */}
                    <div className="card">
                        <div className="card-header">
                            <h6 className="mb-0">{i18n("Horarios Configurados")}</h6>
                        </div>
                        <div className="card-body">
                            {schedules.length === 0 ? (
                                <div className="text-center text-muted py-4">
                                    <span className="material-symbols-outlined mb-2" style={{ fontSize: '48px' }}>
                                        schedule
                                    </span>
                                    <p>{i18n("No hay horarios configurados aún")}</p>
                                </div>
                            ) : (
                                <div className="table-responsive">
                                    <table className="table table-sm">
                                        <thead>
                                            <tr>
                                                <th>{i18n("Día")}</th>
                                                <th>{i18n("Hora Apertura")}</th>
                                                <th>{i18n("Hora Cierre")}</th>
                                                {/* <th>{i18n("Personal Necesario")}</th> */}
                                                <th>{i18n("Acciones")}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {sortedSchedules.map((schedule, index) => (
                                                <tr key={schedule.id}>
                                                    <td>
                                                        <span className="badge bg-primary">
                                                            {getDayDisplayName(schedule.day)}
                                                        </span>
                                                    </td>
                                                    <td>{formatTime(schedule.openTime)}</td>
                                                    <td>{formatTime(schedule.closeTime)}</td>
                                                    {/* <td>
                                                        <span className="badge bg-info">
                                                            {schedule.neededStaff} {schedule.neededStaff === 1 ? 'persona' : 'personas'}
                                                        </span>
                                                    </td> */}
                                                    <td>
                                                        <ButtonComponent
                                                            onClick={() => handleRemoveSchedule(schedule.id)}
                                                            design="dangerOutline"
                                                            icon={<span className="material-symbols-outlined">delete</span>}
                                                            disabled={loading}
                                                            className="btn-sm"
                                                        />
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ScheduleManagement;